'use client';

import { useState, useEffect, useRef } from 'react';
import { FileText, Save, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useSessionNotes, useSaveSessionNotes } from '@/hooks/use-session-notes';
import { useDebounce } from '@/hooks/use-debounce';

interface SessionNotesProps {
  sessionId: string;
}

export function SessionNotes({ sessionId }: SessionNotesProps) {
  const { data, isLoading } = useSessionNotes(sessionId);
  const saveNotes = useSaveSessionNotes(sessionId);
  const [localNote, setLocalNote] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const debouncedNote = useDebounce(localNote, 2000);
  const lastSavedNoteRef = useRef<string>('');

  // Initialize local state from fetched data
  useEffect(() => {
    if (data && !hasInitialized) {
      setLocalNote(data.note);
      lastSavedNoteRef.current = data.note;
      setHasInitialized(true);
    }
  }, [data, hasInitialized]);

  // Auto-save when debounced value changes (only after initialization)
  useEffect(() => {
    if (hasInitialized && debouncedNote !== lastSavedNoteRef.current) {
      lastSavedNoteRef.current = debouncedNote;
      saveNotes.mutateAsync(debouncedNote);
    }
  }, [debouncedNote, hasInitialized, saveNotes]);

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalNote(e.target.value);
  };

  const isSaving = saveNotes.isPending;
  const hasUnsavedChanges = localNote !== data?.note;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Session Notes
          </span>
          {isSaving ? (
            <span className="flex items-center gap-1 text-sm font-normal text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </span>
          ) : hasUnsavedChanges ? (
            <span className="flex items-center gap-1 text-sm font-normal text-amber-600">
              <Save className="w-4 h-4" />
              Unsaved
            </span>
          ) : data?.noteId ? (
            <span className="text-sm font-normal text-green-600">Saved</span>
          ) : null}
        </CardTitle>
        <CardDescription>
          What went well, what to work on next time
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <div className="h-24 bg-muted animate-pulse rounded-md" />
        ) : (
          <>
            {localNote.startsWith('📋') && (
              <p className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1">
                Activity plan pre-filled from template — edit freely
              </p>
            )}
            <Textarea
              value={localNote}
              onChange={handleNoteChange}
              placeholder="What went well? What needs work next session? Any player observations..."
              rows={4}
              className="resize-none"
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
